import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import { useDMQuery } from "../../../utils";
import DiffSection from "./DiffSection";
import DiffHistory from "./DiffHistory";
import DiffHistoryOld from "./DiffHistoryOld";

export const DiffSearch: React.FC = () => {
  const { teacherAssignmentId } = useParams<{ teacherAssignmentId?: string }>();
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState(teacherAssignmentId || "");
  const [activeTab, setActiveTab] = useState<"new" | "old" | null>(null);

  const { data, isLoading } = useDMQuery({
    path: `/manager_new/manage/assignment-diffs/${teacherAssignmentId ?? ""}`,
    queryOptions: {
      enabled: !!teacherAssignmentId,
      staleTime: 1000 * 60 * 15,
      refetchOnWindowFocus: false,
    },
  });

  useEffect(() => {
    setInputValue(teacherAssignmentId || "");
  }, [teacherAssignmentId]);

  // Split results into old and new
  const oldDiffs = useMemo(
    () => (data?.results || []).filter((r: any) => !r.action),
    [data]
  );
  const newDiffs = useMemo(
    () => (data?.results || []).filter((r: any) => r.action),
    [data]
  );

  // Set default tab when data changes
  useEffect(() => {
    if (data?.results?.length > 0) {
      if (data.results.some((r: any) => r.action)) {
        setActiveTab("new");
      } else if (data.results.some((r: any) => !r.action)) {
        setActiveTab("old");
      } else {
        setActiveTab(null);
      }
    } else {
      setActiveTab(null);
    }
  }, [data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputValue.trim();
    if (teacherAssignmentId) {
      navigate("../" + trimmed, { relative: "path", replace: true });
    } else {
      navigate(trimmed, { replace: true });
    }
  };

  return (
    <main className="flex flex-col gap-6 p-6">
      <header className="flex items-center gap-8">
        <h1 className="text-2xl font-bold">Assignment Diff Search</h1>
      </header>
      <form onSubmit={handleSubmit} className="flex items-center gap-2">
        <input
          type="text"
          placeholder="Teacher Assignment ID"
          value={inputValue}
          onChange={handleInputChange}
          className="rounded border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500"
          style={{ minWidth: 220 }}
        />
        <button
          type="submit"
          className="rounded bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
        >
          Search
        </button>
      </form>
      {!isLoading && teacherAssignmentId && data === undefined && (
        <div className="mt-4 text-red-600">Assignment not found</div>
      )}
      {data?.assignment?.sectionInfo && (
        <DiffSection
          sectionInfo={data.assignment.sectionInfo}
          assignmentName={data.assignment.name}
        />
      )}

      {(newDiffs.length > 0 || oldDiffs.length > 0) && (
        <div className="my-4 flex gap-2">
          {newDiffs.length > 0 && (
            <button
              className={`rounded px-4 py-2 ${
                activeTab === "new" ? "bg-indigo-600 text-white" : "bg-gray-200"
              }`}
              onClick={() => setActiveTab("new")}
              type="button"
            >
              Assignment Diffs
            </button>
          )}
          {oldDiffs.length > 0 && (
            <button
              className={`rounded px-4 py-2 ${
                activeTab === "old" ? "bg-indigo-600 text-white" : "bg-gray-200"
              }`}
              onClick={() => setActiveTab("old")}
              type="button"
            >
              Old Assignment Diffs
            </button>
          )}
        </div>
      )}

      {activeTab === "new" && newDiffs.length > 0 && (
        <DiffHistory diffs={newDiffs} />
      )}
      {activeTab === "old" && oldDiffs.length > 0 && (
        <>
          <DiffHistoryOld diffs={oldDiffs} />
        </>
      )}
    </main>
  );
};
